import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { WizardStatus } from '@risksmart-app/domain/src/types/consts';
import {
  Assessment_Activity_Status_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { ownerIds } from 'src/components/form';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import AssessmentRCSAActivityForm from 'src/pages/assessments/forms/assessment-rcsa-activity-form';
import type { AssessmentRCSAActivityFormDataFields } from 'src/pages/assessments/forms/assessment-rcsa-activity-form/assessmentRCSAActivitySchema';

import { useInsertAssessmentActivityWithLinkedItems } from '@/hooks/mutations/assessment-activity/useInsertAssessmentActivityWithLinkedItems';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useInsertWizard } from '@/hooks/mutations/wizard';

import type { AssessmentTypeEnum } from '../../../../types';
import { useAssessmentTypeConfig } from '../../../../useAssessmentTypeConfig';

interface Props {
  assessmentMode: AssessmentTypeEnum;
}

const AssessmentRCSACreateTab: FC<Props> = ({ assessmentMode }) => {
  const navigate = useNavigate();
  const assessmentId = useGetGuidParam('assessmentId');
  const { updateFiles } = useFileUpdate();
  const { insertWizard } = useInsertWizard();
  const { insertAssessmentActivity } =
    useInsertAssessmentActivityWithLinkedItems();
  const {
    routing: { activityRegisterUrl },
  } = useAssessmentTypeConfig(assessmentMode);

  const onSave = async (variables: AssessmentRCSAActivityFormDataFields) => {
    const { files } = variables;

    await Promise.all(
      variables.RiskIds.map(async (riskId) => {
        const result = await insertAssessmentActivity({
          ...variables,
          ParentId: assessmentId,
          Status: Assessment_Activity_Status_Enum.Notstarted,
          LinkedItemIds: [riskId.value],
          CustomAttributeData: variables.CustomAttributeData || undefined,
          RiskId: riskId.value,
          IsRCSA: true,
          ...ownerIds(variables),
        });

        if (!result?.insertAssessmentActivityWithLinkedItems?.Id) {
          throw new Error('Assessment Activity id is missing');
        }

        await updateFiles({
          parentId: result.insertAssessmentActivityWithLinkedItems.Id,
          parentType: Parent_Type_Enum.AssessmentActivity,
          selectedFiles: files,
          originalFiles: [],
        });

        await insertWizard({
          object: {
            RiskId: riskId.value,
            AssessmentId: assessmentId,
            ActivityId: result.insertAssessmentActivityWithLinkedItems.Id,
            Status: WizardStatus.Planned,
          },
        });
      })
    );

    navigate(activityRegisterUrl(assessmentId));
  };

  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(activityRegisterUrl(assessmentId));
    }
  };

  return (
    <AssessmentRCSAActivityForm
      onSave={onSave}
      onDismiss={onDismiss}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
    />
  );
};

export default AssessmentRCSACreateTab;
